import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommunicationCenterService } from 'src/app/services/operations/communication-center.service';
import { CalltrackerService } from 'src/app/services/operations/calltracker.service';
import { ICommCallSummary, ICustomerCallLogItem } from 'src/app/Shared/models/CommunicationCenter';
import { BaseComponent } from 'src/app/Shared/base.component';
import {
  buildCallTranscriptTurns, callDirectionArrow, callScoreClass, callSentimentClass, firstLastName, formatCallDuration, formatCallerId,
  ICallTranscriptTurn
} from '../prospect-customer-call-log/call-log-format';
import { takeUntil } from 'rxjs/operators';

// Arrow-key step on the seek bar, in seconds.
const SEEK_STEP_SECONDS = 5;

// Communication Center - Prospect/Customer Call Log: read-only detail popup for one call, opened from the grid's
// Audio button. Replaces the admin Call Summary (CommCallSummaryComponent) for this popup - that one stays with the
// admin Communication Center screens. Row fields come from the grid; the AI summary, transcript and recording
// details come from GetCallSummary.
@Component({
  selector: 'app-prospect-customer-call-detail',
  templateUrl: './prospect-customer-call-detail.component.html',
  styleUrls: ['./prospect-customer-call-detail.component.scss'],
  standalone: false
})
export class ProspectCustomerCallDetailComponent extends BaseComponent implements OnChanges {
  @Input() item: ICustomerCallLogItem;
  // The popup's tenant/prospect ("Last, First") - names the caller in the transcript.
  @Input() customerName?: string;
  // Emitted once the summary and recording URL have both finished loading (or failed), so the host can show the
  // popup only when there's nothing left to load inside it.
  @Output() loaded = new EventEmitter<void>();
  @ViewChild('audioPlayer') audioPlayer: ElementRef<HTMLAudioElement>;

  summary: ICommCallSummary | null = null;
  audioUrl: string | null = null;
  isPlaying = false;
  audioCurrentTime = 0;
  audioDuration = 0;
  // Loaded after the summary, without holding up the popup's `loaded` event.
  transcriptTurns: ICallTranscriptTurn[] = [];
  transcriptLoading = false;

  constructor(public readonly communicationCenterService: CommunicationCenterService,
              private calltrackerService: CalltrackerService) { super(); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && this.item) {
      this.load();
    }
  }

  private load(): void {
    this.summary = null;
    this.audioUrl = null;
    this.isPlaying = false;
    this.audioCurrentTime = 0;
    this.audioDuration = this.item.Duration ?? 0;
    this.transcriptTurns = [];
    this.transcriptLoading = false;

    this.communicationCenterService.getCallSummary(this.item.PhoneId).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (summary) => {
        this.summary = summary;
        if (!summary.TranscriptUnavailable) {
          this.loadTranscript(summary.PhoneId);
        }
        if (summary.DataExpired || !summary.CallRecordingId || !summary.PhoneSystem) {
          this.loaded.emit();
          return;
        }
        this.loadRecording(summary);
      },
      error: () => this.loaded.emit()
    });
  }

  private loadRecording(summary: ICommCallSummary): void {
    this.calltrackerService.getMediaPlayerURL(
      summary.PhoneNumber,
      summary.PhoneSystem,
      summary.CallRecordingId,
      String(summary.CallCenterIntegrationId ?? 0)
    ).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (url) => {
        this.audioUrl = url || null;
        this.loaded.emit();
      },
      error: () => this.loaded.emit()
    });
  }

  private loadTranscript(phoneId: number): void {
    this.transcriptLoading = true;
    this.communicationCenterService.getCallTranscript(phoneId).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (transcript) => {
        if (phoneId !== this.item?.PhoneId) {
          return;
        }
        this.transcriptTurns = buildCallTranscriptTurns(transcript, this.item.EmployeeName, firstLastName(this.customerName));
        this.transcriptLoading = false;
      },
      error: () => {
        if (phoneId === this.item?.PhoneId) {
          this.transcriptLoading = false;
        }
      }
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  formatDateTime(value: string): string {
    return this.communicationCenterService.formatCallDateTime(value);
  }

  formatDuration(totalSeconds?: number): string {
    return formatCallDuration(totalSeconds);
  }

  formatCallerId(phoneNumber: string): string {
    return formatCallerId(phoneNumber);
  }

  directionArrow(direction: string): string {
    return callDirectionArrow(direction);
  }

  sentimentClass(sentiment: string): string {
    return callSentimentClass(sentiment);
  }

  scoreClass(score?: number): string {
    return callScoreClass(score);
  }

  // ---- Audio player ----------------------------------------------------------------------------------------

  togglePlay(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }

  onAudioPlay(): void {
    this.isPlaying = true;
  }

  onAudioPause(): void {
    this.isPlaying = false;
  }

  onLoadedMetadata(): void {
    const duration = this.audioPlayer?.nativeElement.duration;
    if (duration && isFinite(duration)) {
      this.audioDuration = duration;
    }
  }

  onTimeUpdate(): void {
    this.audioCurrentTime = this.audioPlayer?.nativeElement.currentTime || 0;
  }

  seekAudio(event: MouseEvent, track: HTMLElement): void {
    const rect = track.getBoundingClientRect();
    this.seekTo(((event.clientX - rect.left) / rect.width) * this.audioDuration);
  }

  // Keyboard seeking, same keys as the browser's own audio player: Left/Right (and Down/Up) step 5 seconds,
  // Home/End jump to the start/end.
  onSeekKeydown(event: KeyboardEvent): void {
    const audio = this.audioPlayer?.nativeElement;
    if (!audio) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        this.seekTo(audio.currentTime + SEEK_STEP_SECONDS);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        this.seekTo(audio.currentTime - SEEK_STEP_SECONDS);
        break;
      case 'Home':
        this.seekTo(0);
        break;
      case 'End':
        this.seekTo(this.audioDuration);
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  private seekTo(seconds: number): void {
    const audio = this.audioPlayer?.nativeElement;
    if (!audio || !this.audioDuration) {
      return;
    }
    audio.currentTime = Math.min(Math.max(seconds, 0), this.audioDuration);
  }

  get audioProgressPercent(): number {
    return this.audioDuration ? (this.audioCurrentTime / this.audioDuration) * 100 : 0;
  }
}
